<script>
import { required, maxLength, url } from 'vuelidate/lib/validators'
import { blogPath } from './seo-advanced/validators'

const MAX_FAQ_QUESTION = 500
const MAX_FAQ_ANSWER = 5000
const MAX_CANONICAL_URL = 500
const MAX_LINK_REL_URL = 500
const MAX_REDIRECT_OLD_URL = 500

export default {
  name: 'ArticleSeoAdvancedStep',
  components: {
    InputField: () => import('@/components/general/InputField'),
    FormSection: () => import('@/components/general/FormSection'),
    MetaRobots: () => import('./seo-advanced/MetaRobots'),
    BreadcrumbsPreview: () => import('./seo-advanced/BreadcrumbsPreview'),
    LinkRelsList: () => import('./seo-advanced/LinkRelsList'),
    RedirectsList: () => import('./seo-advanced/RedirectsList'),
    FaqList: () => import('./seo-advanced/FaqList')
  },

  props: {
    formData: {
      type: Object,
      required: true
    },

    breadcrumbLabels: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      MAX_FAQ_QUESTION,
      MAX_FAQ_ANSWER,
      MAX_CANONICAL_URL,
      MAX_LINK_REL_URL,
      MAX_REDIRECT_OLD_URL
    }
  },

  validations () {
    return {
      formData: {
        faqs: {
          $each: {
            question: {
              required,
              maxLength: maxLength(MAX_FAQ_QUESTION)
            },

            answer: {
              required,
              maxLength: maxLength(MAX_FAQ_ANSWER)
            }
          }
        },

        link_rels: {
          $each: {
            url: {
              required,
              url,
              maxLength: maxLength(MAX_LINK_REL_URL)
            }
          }
        },

        canonical_url: {
          url,
          maxLength: maxLength(MAX_CANONICAL_URL)
        },

        redirects: {
          $each: {
            old_url: {
              required,
              blogPath,
              maxLength: maxLength(MAX_REDIRECT_OLD_URL)
            }
          }
        }
      }
    }
  },

  computed: {
    faqs () {
      return Array.isArray(this.formData.faqs) ? this.formData.faqs : []
    },

    redirectDestination () {
      return this.formData.slug ? `/blog/${this.formData.slug}` : ''
    },

    duplicateRedirectIndexes () {
      const redirects = Array.isArray(this.formData.redirects) ? this.formData.redirects : []
      const seen = {}
      const dupes = []

      redirects.forEach((item, index) => {
        const key = (item.old_url || '').trim().toLowerCase()

        if (!key) return

        if (seen[key] !== undefined) {
          if (dupes.indexOf(seen[key]) === -1) dupes.push(seen[key])
          dupes.push(index)
        } else {
          seen[key] = index
        }
      })

      return dupes
    }
  },

  methods: {
    // eslint-disable-next-line vue/no-unused-properties -- called from parent via $refs.currentStep.validate()
    validate () {
      this.$v.$touch()

      return !this.$v.$invalid && this.duplicateRedirectIndexes.length === 0
    },

    emit (patch) {
      this.$emit('input', patch)
    },

    onCanonicalUrlInput (value) {
      this.emit({ canonical_url: value })
    }
  }
}
</script>

<template>
  <form
    class="article-content-form"
    @submit.prevent
  >
    <FormSection :title="$t('articles.form:faq.section')">
      <p class="article-faq-help">
        {{ $t('articles.form:faq.help') }}
      </p>

      <FaqList
        :items="faqs"
        :validation="$v.formData.faqs"
        :max-question="MAX_FAQ_QUESTION"
        :max-answer="MAX_FAQ_ANSWER"
        @input="(next) => emit({ faqs: next })"
      />
    </FormSection>

    <FormSection :title="$t('articles.form:breadcrumbs.section')">
      <p class="article-section-subtitle">
        {{ $t('articles.form:breadcrumbs.help') }}
      </p>

      <BreadcrumbsPreview :labels="breadcrumbLabels" />
    </FormSection>

    <FormSection :title="$t('articles.form:linkrel.section')">
      <p class="article-section-subtitle">
        {{ $t('articles.form:linkrel.help') }}
      </p>

      <LinkRelsList
        :items="formData.link_rels"
        :validation="$v.formData.link_rels"
        :max-url="MAX_LINK_REL_URL"
        @input="(next) => emit({ link_rels: next })"
      />
    </FormSection>

    <FormSection :title="$t('articles.form:redirect.section')">
      <p class="article-section-subtitle">
        {{ $t('articles.form:redirect.help') }}
      </p>

      <RedirectsList
        :items="formData.redirects"
        :validation="$v.formData.redirects"
        :duplicate-indexes="duplicateRedirectIndexes"
        :max-url="MAX_REDIRECT_OLD_URL"
        :destination="redirectDestination"
        @input="(next) => emit({ redirects: next })"
      />
    </FormSection>

    <FormSection :title="$t('articles.form:canonical.section')">
      <p class="article-section-subtitle">
        {{ $t('articles.form:canonical.help') }}
      </p>

      <InputField
        :label="$t('articles.form:canonical.section')"
        :value="formData.canonical_url"
        :placeholder="$t('articles.form:canonical.placeholder')"
        :validation="$v.formData.canonical_url"
        :counter="MAX_CANONICAL_URL"
        @input="onCanonicalUrlInput"
      />
    </FormSection>

    <FormSection :title="$t('articles.form:metarobots.section')">
      <MetaRobots
        :values="formData"
        @input="emit"
      />
    </FormSection>
  </form>
</template>
